/**
 * The sign-in flow against the platform auth server (ADR-017): a public client running
 * authorization-code + PKCE, with PAR. The session holder is in memory; everything that touches
 * the host — which issuer and client id, where the code comes back, how the in-flight login
 * and the session survive the round-trip, how the authorize URL is opened — comes through the
 * `Platform` port (ADR-029 §2): a full-page redirect with sessionStorage on the web, an external
 * user agent with the shell's own stores in the native apps, which also keep the session for
 * the token's lifetime (ADR-029 §4).
 */
import { en } from '../i18n/en';
import type { Messages } from '../i18n/messages';
import type { Platform } from '../platform/platform';
import { authorizeUrl, emailOf, parRequest, tokenRequest } from './flow';
import { challengeS256, generateVerifier, randomToken } from './pkce';

export { emailOf } from './flow';

export interface Session {
  readonly accessToken: string;
  /** Display-only (decoded, unverified — the API re-validates every call). */
  readonly email: string | null;
  /** When the token expires (ms since the epoch), from the token response's `expires_in`. */
  readonly expiresAt?: number;
}

let current: Session | null = null;

/**
 * A sign-out has been announced but the session is still held: the shells unregister the push
 * device first (a DELETE that needs the token — ADR-030 §2) and end the session after. In that
 * window no new work may start on the token — a plan link arriving then is remembered for the
 * next sign-in, not opened (`isSignedIn`).
 */
let signingOut = false;

export function getSession(): Session | null {
  return current;
}

export function getAccessToken(): string | null {
  return current?.accessToken ?? null;
}

/** Whether new work may start on the session: one is held and no sign-out is under way. */
export function isSignedIn(): boolean {
  return current !== null && !signingOut;
}

/** Announces a sign-out whose farewell calls still need the token; ends with the session. */
export function markSigningOut(): void {
  signingOut = true;
}

export function setSession(session: Session | null): void {
  current = session;
  signingOut = false;
}

export function clearSession(): void {
  current = null;
  signingOut = false;
}

/** Which step of the round-trip failed — the key the banner words it under (`Messages['auth']`). */
export type AuthFlowErrorCode = 'par-failed' | 'none-in-progress' | 'state-mismatch' | 'token-failed';

/**
 * A sign-in round-trip failed in a way the user can only fix by starting over. Carries the
 * step as a code (and the auth server's status where one answered) so the banner can word it in
 * the current language at render time (ADR-031); the `message` is the English wording, for
 * logs and tests.
 */
export class AuthFlowError extends Error {
  constructor(
    readonly code: AuthFlowErrorCode,
    /** The auth server's HTTP status, for the two steps that call it; null otherwise. */
    readonly status: number | null = null,
  ) {
    super(describeAuthFlowError({ code, status }, en.auth));
  }
}

/** The banner text for a failed round-trip in the given catalogue (English by default). */
export function describeAuthFlowError(
  error: Pick<AuthFlowError, 'code' | 'status'>,
  auth: Messages['auth'] = en.auth,
): string {
  switch (error.code) {
    case 'par-failed':
      return auth.parFailed(String(error.status));
    case 'none-in-progress':
      return auth.noneInProgress;
    case 'state-mismatch':
      return auth.stateMismatch;
    case 'token-failed':
      return auth.tokenFailed(String(error.status));
  }
}

/**
 * Starts the code flow: remembers the in-flight login on the platform, pushes the
 * authorization request (PAR) and opens the auth server's authorize endpoint, which bounces
 * on to the chosen identity provider.
 */
export async function beginLogin(platform: Platform, idp = 'google'): Promise<void> {
  const verifier = generateVerifier();
  const login = { verifier, state: randomToken(), nonce: randomToken() };
  await platform.savePending(login);

  const par = parRequest(platform.auth, await challengeS256(verifier), login);
  const res = await fetch(par.url, { method: 'POST', body: par.body });
  if (!res.ok) throw new AuthFlowError('par-failed', res.status);
  const pushed = (await res.json()) as { request_uri: string };

  await platform.openAuth(authorizeUrl(platform.auth, pushed.request_uri, idp));
}

/**
 * Completes the flow once the code came back: correlates the response with the pending login,
 * exchanges the code (PKCE), establishes the in-memory session and hands it to the platform
 * to persist where it does.
 */
export async function completeLogin(
  platform: Platform,
  query: URLSearchParams,
  now: () => number = () => Date.now(),
): Promise<Session> {
  const pending = await platform.takePending();
  if (pending === null) throw new AuthFlowError('none-in-progress');

  const code = query.get('code');
  if (code === null || query.get('state') !== pending.state) {
    throw new AuthFlowError('state-mismatch');
  }

  const exchange = tokenRequest(platform.auth, code, pending.verifier);
  const res = await fetch(exchange.url, { method: 'POST', body: exchange.body });
  if (!res.ok) throw new AuthFlowError('token-failed', res.status);
  const tokens = (await res.json()) as {
    access_token: string;
    id_token?: string;
    expires_in?: number;
  };

  const session: Session = {
    accessToken: tokens.access_token,
    email: emailOf(tokens.id_token),
    ...(typeof tokens.expires_in === 'number' ? { expiresAt: now() + tokens.expires_in * 1000 } : {}),
  };
  setSession(session);
  await platform.storeSession(session);
  return session;
}

/**
 * Adopts the session an install persisted (ADR-029 §4 — the shells keep it for the token's
 * lifetime so an app kill does not re-prompt), unless it has expired or never carried an
 * expiry, in which case it is forgotten.
 */
export async function restoreSession(
  platform: Platform,
  now: () => number = () => Date.now(),
): Promise<Session | null> {
  const stored = await platform.loadSession();
  if (stored === null) return null;
  if (stored.expiresAt === undefined || stored.expiresAt <= now()) {
    await platform.storeSession(null);
    return null;
  }
  setSession(stored);
  return stored;
}

/** Ends the session everywhere this install keeps it. */
export async function endSession(platform: Platform): Promise<void> {
  clearSession();
  await platform.storeSession(null);
}
